import React, { useState, useEffect } from "react";
import { UserCheck, X, Plus, Save, Activity, Minus } from "lucide-react";
import Mo3tiButton from "../../layout/Mo3tiButton";
import ReusableDropdown from "../../../../../components/ReusableDropdown";
import { apiFetch } from "../../../../../context/AuthContext";
import AddDeductionForm from "./AddDeductionForm";
import DeductionsList from "./DeductionsList";
import { calculateGrossFromNet } from "../../../utils/marginalTax";


// Net vs Gross daily rate for a Days-based deduction line. Mirrors
// AllowancesModal's resolveDailyRate (payrollService.js's resolveEmployeeBaseGross
// on the backend): prefer the employee's actual stored gross salary for this
// payroll when we have it, otherwise grossing up basicSalary the same way the
// backend does, so a Gross-mode day entry is priced at the employee's real
// gross rate instead of always falling back to the net one.
const resolveDailyRate = (employee, isGross) => {
  const basicSalary = Number(employee?.basicSalary) || 0;
  const netDailyRate = employee?.dailyRate
    ? Number(employee.dailyRate)
    : basicSalary / 30;

  if (!isGross) return netDailyRate;

  // Always gross up THIS payroll's basicSalary rather than trusting the
  // employee's stored `grossSalary` field — that field can go stale (a raise
  // applied to basicSalary that grossSalary hasn't caught up with yet, or a
  // "new" gross set by some other flow), and pricing a Gross-mode day entry
  // off a mismatched gross salary silently mispreces it. basicSalary +
  // insuranceSalary are always this payroll's real, current figures.
  const insuranceSalary = Number(employee?.insuranceSalary) || 0;
  const grossedUp = calculateGrossFromNet(basicSalary, insuranceSalary);
  return grossedUp > 0 ? grossedUp / 30 : netDailyRate;
};

const DeductionsModal = ({
  isOpen,
  employee,
  onClose,
  API_BASE_URL,
  currentTheme,
  t,
  language,
  theme,
  isRTL,
  showAlert,
  screenSize,
}) => {
    const [deductions, setDeductions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showAddForm, setShowAddForm] = useState(false);
    const [newDeduction, setNewDeduction] = useState({
      type: "",
      description: "",
      amount: "",
      days: "",
      isPreTax: false,
    });
    const [editingDeductionId, setEditingDeductionId] = useState(null);
    const [lateCalcLoading, setLateCalcLoading] = useState(false);
    const [lateRates, setLateRates] = useState(null); // { dailyRate, lateDeductionPerHour }
    const gridCols = screenSize === "xs" ? 1 : screenSize === "sm" ? 2 : 4;

    useEffect(() => {
      if (isOpen && employee?.id) {
        fetchDeductions();
      }
    }, [isOpen, employee?.id]);

    const fetchDeductions = async () => {
      setLoading(true);
      try {
        const url = `${API_BASE_URL}/payrollX/${employee.id}/deductions`;
        const response = await apiFetch(url, { method: "GET" });

        if (response.ok) {
          const result = await response.json();
          setDeductions(result?.data?.deductions || []);
        }
      } catch (err) {
        console.error("Error fetching deductions:", err);
      } finally {
        setLoading(false);
      }
    };

    // Live preview: recompute `amount` the moment Days drives a value, the
    // same way AllowancesModal does — so entering a day count is enough on
    // its own; you never have to also type the Amount by hand. Skips
    // late_arrival, which has its own dedicated hours→amount handler
    // (handleLateHoursChange) driven by the company's configured late rate.
    useEffect(() => {
      if (newDeduction.type === "late_arrival") return;
      const days = newDeduction.days ? parseFloat(newDeduction.days) : null;
      if (!days) return;

      const dailyRate = resolveDailyRate(employee, newDeduction.isPreTax);
      const computed = dailyRate * days;

      if (computed > 0) {
        const rounded = computed.toFixed(2);
        setNewDeduction((prev) =>
          prev.amount === rounded ? prev : { ...prev, amount: rounded },
        );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newDeduction.days, newDeduction.type, newDeduction.isPreTax]);

    const handleAddDeduction = async () => {
      if (!newDeduction.type) {
        showAlert(t("important"), t("fillTypeAmount"), "info");
        return;
      }

      let days = newDeduction.days ? parseFloat(newDeduction.days) : null;
      let amount = newDeduction.amount ? parseFloat(newDeduction.amount) : 0;

      // Always calculate amount from days if days is provided. Gross-mode
      // entries (isPreTax) price off the employee's real gross daily rate,
      // not the net one — matches AllowancesModal's resolveDailyRate and the
      // (Net)/(Gross) column pair the bulk import template and backend
      // already use for the same distinction. late_arrival keeps using its
      // own dedicated rate (fetched from /late-deduction-calc), unaffected
      // by the Gross/Net toggle.
      if (days && (!amount || amount === 0)) {
        let dailyRate = 0;
        if (
          newDeduction.type === "late_arrival" &&
          lateRates &&
          lateRates.dailyRate
        ) {
          dailyRate = lateRates.dailyRate;
        } else {
          dailyRate = resolveDailyRate(employee, newDeduction.isPreTax);
        }
        amount = days * dailyRate;
      }

      // If neither amount nor days is valid, show alert
      if (
        (!amount || isNaN(amount) || amount === 0) &&
        (!days || isNaN(days) || days === 0)
      ) {
        showAlert(t("important"), t("fillTypeAmount"), "info");
        return;
      }

      setLoading(true);
      try {
        const isEditing = !!editingDeductionId;
        const url = isEditing 
          ? `${API_BASE_URL}/payrollX/deductions/${editingDeductionId}`
          : `${API_BASE_URL}/payrollX/${employee.id}/deductions`;
          
        const response = await apiFetch(url, {
          method: isEditing ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            type: newDeduction.type,
            description: newDeduction.description,
            amount: amount,
            days: days,
            isPreTax: !!newDeduction.isPreTax,
          }),
        });

        if (response.ok) {
          await fetchDeductions();
          setNewDeduction({
            type: "",
            description: "",
            amount: "",
            days: "",
            isPreTax: false,
          });
          setEditingDeductionId(null);
          setShowAddForm(false);
          showAlert(t("success"), isEditing ? t("deductionUpdatedSuccess") || "Deduction updated successfully" : t("deductionAddedSuccess"), "success");
        }
      } catch (err) {
        console.error("Error adding deduction:", err);
        showAlert(t("error"), t("errorAddingDeduction"), "error");
      } finally {
        setLoading(false);
      }
    };

    const calculateTotal = () => {
      return deductions.reduce((sum, d) => sum + Number(d.amount || 0), 0);
    };

    const fetchLateDeduction = async () => {
      if (!employee?.id) return;
      setLateCalcLoading(true);
      try {
        const url = `${API_BASE_URL}/payrollX/${employee.id}/late-deduction-calc`;
        const response = await apiFetch(url, { method: "GET" });
        if (response.ok) {
          const result = await response.json();
          const data = result.data || result;
          setLateRates({
            dailyRate: data.dailyRate || 0,
            lateDeductionPerHour: data.lateDeductionPerHour || 1,
          });
          // Reset amount so user enters hours
          setNewDeduction((prev) => ({
            ...prev,
            type: "late_arrival",
            amount: "",
            description: t("lateArrivalCalcDesc"),
          }));
        } else {
          console.error("Failed to fetch late deduction rates");
          setLateRates(null);
        }
      } catch (err) {
        console.error("Error fetching late deduction rates:", err);
        setLateRates(null);
      } finally {
        setLateCalcLoading(false);
      }
    };

    const handleLateHoursChange = (hours) => {
      const h = Number(hours) || 0;
      if (lateRates) {
        // Half a day per hour late: 1 hour = 0.5 days
        const amount = (h / 2) * lateRates.dailyRate;
        setNewDeduction((prev) => ({
          ...prev,
          days: hours,
          amount: amount > 0 ? amount.toFixed(2) : "",
          description:
            h > 0
              ? `${t("lateArrivalCalcDesc")} - ${h} ${language === "ar" ? "ساعة" : "hrs"}`
              : t("lateArrivalCalcDesc"),
        }));
      } else {
        setNewDeduction((prev) => ({ ...prev, days: hours }));
      }
    };

    const handleDeleteDeduction = async (deductionId) => {
      if (!window.confirm(t("confirmDeleteDeduction") || "Are you sure you want to delete this deduction?")) {
        return;
      }
      setLoading(true);
      try {
        const url = `${API_BASE_URL}/payrollX/deductions/${deductionId}`;
        const response = await apiFetch(url, { method: "DELETE" });
        if (response.ok) {
          showAlert(t("success"), t("deductionDeletedSuccess") || "Deduction deleted successfully", "success");
          fetchDeductions();
          if (editingDeductionId === deductionId) {
            setEditingDeductionId(null);
            setShowAddForm(false);
          }
        } else {
          showAlert(t("error"), t("errorDeletingDeduction") || "Failed to delete deduction", "error");
        }
      } catch (err) {
        console.error("Error deleting deduction:", err);
        showAlert(t("error"), t("errorDeletingDeduction") || "Failed to delete deduction", "error");
      } finally {
        setLoading(false);
      }
    };

    const handleEditClick = (deduction) => {
      setEditingDeductionId(deduction._id || deduction.id);
      setNewDeduction({
        type: deduction.type,
        description: deduction.description || "",
        amount: deduction.amount,
        days: deduction.days || "",
        isPreTax: !!deduction.isPreTax,
      });
      setShowAddForm(true);
    };

    const handleToggleForm = () => {
      if (showAddForm) {
        setShowAddForm(false);
        setEditingDeductionId(null);
        setNewDeduction({ type: "", description: "", amount: "", days: "", isPreTax: false });
      } else {
        setShowAddForm(true);
      }
    };

    if (!isOpen) return null;

    const formStyles = {
      modal: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.8)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        zIndex: 3000,
        padding: "20px",
        overflowY: "auto",
        scrollbarWidth: "none", // للـ Firefox
        msOverflowStyle: "none", // للـ IE و Edge
        WebkitOverflowScrolling: "touch", // للـ iOS
        scrollbarWidth: "none", // للـ Firefox
        msOverflowStyle: "none", // للـ IE و Edge
      },
      modalContent: {
        background: currentTheme.colors.cardBackground,
        backdropFilter: "blur(25px)",
        border: `2px solid ${currentTheme.colors.border}`,
        borderRadius: "24px",
        padding: "40px",
        width: "100%",
        maxWidth: "900px",
        margin: "20px auto",
        maxHeight: "90vh",
        overflowY: "auto",
        scrollbarWidth: "none", // للـ Firefox
        msOverflowStyle: "none", // للـ IE و Edge
        WebkitOverflowScrolling: "touch", // للـ iOS
        scrollbarWidth: "none", // للـ Firefox
        msOverflowStyle: "none", // للـ IE و Edge
        boxShadow: currentTheme.shadows.card,
      },
      header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "32px",
        paddingBottom: "20px",
        borderBottom: `2px solid ${currentTheme.colors.border}`,
      },
      statCard1: {
        background:
          theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.9)",
        padding: "1.55rem",
        marginTop: -40,
        marginLeft: 5,
        borderRadius:
          screenSize === "xs"
            ? "1rem"
            : screenSize === "sm"
              ? "1.25rem"
              : "1rem",
        transition: "all 0.3s ease",
        boxShadow:
          theme === "dark"
            ? "0 0.5rem 2rem rgba(0,0,0,0.3), inset 0 0.0625rem 0 rgba(255,255,255,0.1)"
            : "0 0.5rem 2rem rgba(0,0,0,0.1)",
        backdropFilter: theme === "dark" ? "blur(0.625rem)" : "blur(0px)",
        WebkitBackdropFilter: theme === "dark" ? "blur(0.625rem)" : "blur(0px)",
        border:
          theme === "dark"
            ? "1px solid rgba(255,255,255,0.15)"
            : "1px solid rgba(0,0,0,0.05)",
        marginBottom: 70,
        cursor: "pointer",
      },

      title: {
        color: currentTheme.colors.textPrimary,
        fontSize: "28px",
        fontWeight: "800",
        margin: 0,
        display: "flex",
        alignItems: "center",
        gap: "12px",
      },
      section: {
        background: currentTheme.colors.inputBackground,
        borderRadius: "20px",
        padding: "24px",
        border: `2px solid ${currentTheme.colors.border}`,
        marginBottom: "24px",
      },
      deductionCard: {
        position: "relative",
        overflow: "hidden",
        background: currentTheme.colors.cardBackground,
        borderRadius: "14px",
        padding: "18px 20px 18px 24px",
        border: `1px solid ${currentTheme.colors.borderLight}`,
        marginBottom: "14px",
        transition: "all 0.25s ease",
        boxShadow: theme === "dark" ? "0 2px 8px rgba(0,0,0,0.18)" : "0 2px 8px rgba(0,0,0,0.04)",
      },
      formGrid: {
        display: "grid",
        gridTemplateColumns: `repeat(${gridCols}, 1fr)`, // ← هتبقى 4 columns
        gap: "16px",
        marginBottom: "20px",
      },
      formGroup: {
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      },
      label: {
        color: currentTheme.colors.textSecondary,
        fontSize: "14px",
        fontWeight: "600",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
      },
      input: {
        background: currentTheme.colors.inputBackground,
        border: `2px solid ${currentTheme.colors.border}`,
        borderRadius: "12px",
        padding: "12px 16px",
        color: currentTheme.colors.textPrimary,
        fontSize: "14px",
        fontWeight: "500",
        transition: "all 0.3s ease",
        outline: "none",
      },
      totalCard: {
        background: `${currentTheme.colors.danger}10`,
        border: `2px solid ${currentTheme.colors.danger}40`,
        borderRadius: "16px",
        padding: "24px",
        textAlign: "center",
      },
    };

    return (
      <div
        style={formStyles.modal}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div style={formStyles.modalContent}>
          <div style={formStyles.header}>
            <h1 style={formStyles.title}>
              <UserCheck size={32} />
              {t("employeeDeductions")}{" "}
              {employee?.employeeName?.split(" ").slice(0, 2).join(" ")}
            </h1>
            {/* <button
            style={{
              background: "rgba(239, 68, 68, 0.1)",
              border: "2px solid rgba(239, 68, 68, 0.3)",
              borderRadius: "12px",
              padding: "12px",
              cursor: "pointer",
              color: "#ef4444",
              transition: "all 0.3s ease",
            }}
            onClick={onClose}
          >
          </button> */}
          </div>

          {/* Add Deduction Form */}
          <AddDeductionForm
            t={t}
            theme={theme}
            formStyles={formStyles}
            showAddForm={showAddForm}
            setShowAddForm={handleToggleForm}
            newDeduction={newDeduction}
            setNewDeduction={setNewDeduction}
            lateCalcLoading={lateCalcLoading}
            fetchLateDeduction={fetchLateDeduction}
            handleLateHoursChange={handleLateHoursChange}
            handleAddDeduction={handleAddDeduction}
            loading={loading}
            language={language}
            editingDeductionId={editingDeductionId}
          />

          {/* Deductions List */}
          <DeductionsList
            t={t}
            currentTheme={currentTheme}
            formStyles={formStyles}
            isRTL={isRTL}
            loading={loading}
            deductions={deductions}
            onDeleteDeduction={handleDeleteDeduction}
            onEditDeduction={handleEditClick}
          />
        </div>
      </div>
    );
  };

export default DeductionsModal;